import { Injectable, inject } from "@angular/core";
import { marker } from "@jsverse/transloco-keys-manager/marker";
import { DxFormComponent } from "devextreme-angular";
import DataSource from "devextreme/data/data_source";
import CustomStore from "devextreme/data/custom_store";
import DevExpress from "devextreme";
import { compact, forOwn, trim } from "lodash-es";
import { last } from "lodash";
import { lastValueFrom } from "rxjs";
import { CustomerBackend } from "src/app/backend/api/customer.backend";
import { AttachmentBackend } from "src/app/backend/api/attachment.backend";
import { HealthRecordBackend } from "src/app/backend/api/health-record.backend";
import { MemberBackend } from "src/app/backend/api/member.backend";
import { PetBackend } from "src/app/backend/api/pet.backend";
import { PhysicianBackend } from "src/app/backend/api/physician.backend";
import { SoapTemplateBackend } from "src/app/backend/api/soap-template.backend";
import { StorageBackend } from "src/app/backend/api/storage/storage.backend";
import {
  HealthRecord,
  HealthRecordAttachment,
  HealthRecordWithAttachmentsAndTicketExtended
} from "src/app/backend/models/health-record";
import {
  Attachment,
  AttachmentV2
} from "src/app/backend/models/attachment/attachment";
import { Customer } from "src/app/backend/models/customer/customer";
import { Physician } from "src/app/backend/models/physician";
import { Pet } from "src/app/backend/models/pet/pet";
import { sntConfirm } from "src/app/common/dialog";
import { DocumentService } from "src/app/services/document.service";
import { PopupService } from "src/app/services/popup/popup.service";
import { PrintService } from "src/app/services/print/print.service";
import { ReportService } from "src/app/services/report.service";
import { TemplateService } from "src/app/services/template.service";
import {
  ensureAttachmentSelectionWithinLimit,
  getSelectedAttachmentIds,
  syncAttachmentSelectionOrder
} from "src/app/support/health-record-attachment-selection";
import { getMimeType, isImage, isPdf } from "src/app/support/file";
import { soapTemplateWithHealthRecordoToTemplateItems } from "src/app/support/health-record";
import { Nullable } from "src/app/support/nullable";
import { urlToBlob, urlToDataUrl } from "src/app/support/blob";
import { pdfMergeDocuments } from "src/app/support/pdf";
import { sntTranslate } from "src/app/support/translate";
import { uuid } from "src/app/support/uuid";
import { getCustomerNominative } from "src/app/support/customer";
import {
  resolveExternalPhysicianSendEmail,
  resolveReferringPhysicianSendEmail
} from "src/app/support/resolve-external-physician-email";
import { sift } from "radash";

const templatePrintCanBeLongerThenExpectedFn = () =>
  `<div class="mt-4">
                <span class="text-slate-400 italic text-xs">${sntTranslate(
                  marker(
                    "La stampa potrebbe impiegare fino ad un minuto se presenti più visite"
                  )
                )}</span>
                    </div>`;
@Injectable()
export class HealthRecordService {
  private readonly soapTemplateBackend = inject(SoapTemplateBackend);
  private readonly memberBackend = inject(MemberBackend);
  private readonly templateService = inject(TemplateService);
  private readonly patientBackend = inject(PetBackend);
  private readonly customerBackend = inject(CustomerBackend);
  private readonly reportService = inject(ReportService);
  private readonly documentService = inject(DocumentService);
  private readonly printService = inject(PrintService);
  private readonly healthRecordBackend = inject(HealthRecordBackend);
  private readonly popupService = inject(PopupService);
  private readonly physicianBackend = inject(PhysicianBackend);
  private readonly storageBackend = inject(StorageBackend);
  private readonly attachmentBackend = inject(AttachmentBackend);

  private async hydrateAttachmentsWhenMissing(row: {
    id: string;
    attachments?: HealthRecordAttachment[];
  }): Promise<void> {
    if (row.attachments !== undefined) {
      return;
    }

    row.attachments = await lastValueFrom(
      this.healthRecordBackend.loadAttachments(row.id)
    );
  }

  async generatePdfBlob({
    patientId,
    healthRecord,
    includeFollowUp,
    selectedAttachments
  }: {
    patientId: string;
    healthRecord: HealthRecordWithAttachmentsAndTicketExtended;
    includeFollowUp: boolean;
    selectedAttachments?: string[];
  }): Promise<Blob> {
    await this.hydrateAttachmentsWhenMissing(healthRecord);

    const healthRecordsToPrint = [healthRecord];

    if (includeFollowUp) {
      const children = await lastValueFrom(
        this.healthRecordBackend.listAllChildrenDeepExtendedWithAttachmentsByPatient(
          patientId,
          healthRecord.id
        )
      );

      children.forEach((x) => {
        healthRecordsToPrint.push(
          x as HealthRecordWithAttachmentsAndTicketExtended
        );
      });
    }

    const blobs = [];

    for (const healthRecordToPrint of healthRecordsToPrint) {
      blobs.push(
        await this.healthRecordToLettterHeadPdfBlob(
          healthRecordToPrint,
          selectedAttachments ?? []
        )
      );
    }

    return await pdfMergeDocuments(blobs);
  }

  async toPdfHealthRecord({
    patientId,
    healthRecord,
    ignoreFollowUp,
    selectedAttachments
  }: {
    patientId: string;
    healthRecord: HealthRecordWithAttachmentsAndTicketExtended;
    ignoreFollowUp?: boolean;
    selectedAttachments?: string[];
  }): Promise<Blob | null> {
    if (ignoreFollowUp) {
      return await this.generatePdfBlob({
        patientId: patientId,
        healthRecord: healthRecord,
        includeFollowUp: false,
        selectedAttachments: selectedAttachments ?? []
      });
    }

    await this.hydrateAttachmentsWhenMissing(healthRecord);

    const attachmentsList = healthRecord.attachments ?? [];

    const sendableAttachments = attachmentsList.filter(
      (x) =>
        !(<AttachmentV2>x).signable &&
        !(<AttachmentV2>x).signature &&
        (isImage(x.name) || isPdf(x.name))
    );

    const pdfSelectionOrder: string[] = [];

    return new Promise<Blob | null>((res) => {
      this.popupService.createEntity<{ includeFollowUp: boolean }>({
        title: sntTranslate(marker("Stampa visita")),
        initialFormData: {
          includeFollowUp: false
        },
        modalConfig: { dialog: true, width: 620, height: 720 },
        formItems: compact([
          {
            dataField: "includeFollowUp",
            editorType: "dxCheckBox",
            label: {
              text: sntTranslate(marker("Includi i follow up")),
              showColon: false
            }
          },
          sendableAttachments.length > 0
            ? {
                itemType: "group",
                colCount: 1,
                items: sift([
                  {
                    template: (dxEvent: any, element: HTMLElement) => {
                      element.innerHTML = `<div class="flex flex-row items-center mt-4">
                  <span class="text-sm text-slate-400">${sntTranslate(
                    marker("Seleziona gli allegati da includere:")
                  )}</span></div>`;
                    }
                  },
                  sendableAttachments.length > 1
                    ? <DevExpress.ui.dxForm.Item>{
                        dataField: "selectAllAttachments",
                        editorType: "dxCheckBox",
                        editorOptions: {
                          text: sntTranslate(marker("Seleziona tutti")),
                          onValueChanged: ({
                            dxEvent: _dxEvent,
                            dxForm
                          }: {
                            dxEvent: unknown;
                            dxForm: DxFormComponent;
                            formData: Record<string, boolean>;
                          }) => {
                            const formData = dxForm.formData as Record<
                              string,
                              boolean
                            >;
                            const selectAllAttachments: boolean =
                              formData["selectAllAttachments"];
                            sendableAttachments.forEach(({ id }) => {
                              dxForm.instance.updateData(
                                `attachment-${id}`,
                                selectAllAttachments
                              );
                            });
                          }
                        },
                        label: {
                          text: sntTranslate(marker("Seleziona tutti")),
                          location: "left",
                          showColon: false,
                          visible: false
                        }
                      }
                    : null,
                  {
                    itemType: "group",
                    colCount: 2,
                    items: [
                      ...(sendableAttachments.map(
                        (x) =>
                          <DevExpress.ui.dxForm.Item>{
                            dataField: `attachment-${x.id}`,
                            editorType: "dxCheckBox",
                            editorOptions: {
                              text: x.name,
                              onValueChanged: ({
                                dxEvent: _dxEvent,
                                dxForm
                              }: {
                                dxEvent: unknown;
                                dxForm: DxFormComponent;
                                formData: Record<string, boolean>;
                              }) => {
                                const formData = dxForm.formData as Record<
                                  string,
                                  boolean
                                >;
                                const selected: boolean =
                                  !!formData[`attachment-${x.id}`];

                                if (isPdf(x.name)) {
                                  syncAttachmentSelectionOrder(
                                    pdfSelectionOrder,
                                    x.id,
                                    selected
                                  );
                                }

                                if (sendableAttachments.length > 1) {
                                  dxForm.instance.updateData(
                                    "selectAllAttachments",
                                    sendableAttachments.every(
                                      ({ id }) => formData[`attachment-${id}`]
                                    )
                                  );
                                }
                              }
                            },
                            label: {
                              text: x.name,
                              location: "left",
                              showColon: false,
                              visible: false
                            }
                          }
                      ) || [])
                    ]
                  },
                  {
                    template: templatePrintCanBeLongerThenExpectedFn
                  }
                ])
              }
            : null,
          sendableAttachments.length === 0
            ? {
                template: () =>
                  `<span class="text-slate-400 italic text-xs">${sntTranslate(
                    marker(
                      "La stampa potrebbe impiegare fino ad un minuto se presenti più visite"
                    )
                  )}</span>`
              }
            : null
        ]),
        create: async (data) => {
          await ensureAttachmentSelectionWithinLimit(data);

          const selectedAttachmentIds: string[] =
            getSelectedAttachmentIds(data);

          const schedaBlob: Blob = await this.generatePdfBlob({
            patientId: patientId,
            healthRecord: healthRecord,
            includeFollowUp: data.includeFollowUp,
            selectedAttachments: selectedAttachmentIds
          });

          const orderedPdfAttachmentIds: string[] = pdfSelectionOrder.filter(
            (id) => selectedAttachmentIds.includes(id)
          );

          const mergedPdfDocumentBlob: Blob =
            await this.#mergePrintBlobWithSelectedPdfAttachments(
              schedaBlob,
              healthRecord,
              orderedPdfAttachmentIds
            );

          res(mergedPdfDocumentBlob);
        },
        onCancel: async () => {
          res(null);
        }
      });
    });
  }

  async printHealthRecord({
    patientId,
    healthRecord
  }: {
    patientId: string;
    healthRecord: HealthRecordWithAttachmentsAndTicketExtended;
  }): Promise<void> {
    const blob = await this.toPdfHealthRecord({ patientId, healthRecord });

    if (blob) {
      await this.printService.printPdf(Promise.resolve(blob));
    }
  }

  async #mergePrintBlobWithSelectedPdfAttachments(
    schedaBlob: Blob,
    healthRecord: HealthRecordWithAttachmentsAndTicketExtended,
    orderedPdfAttachmentIds: string[]
  ): Promise<Blob> {
    if (orderedPdfAttachmentIds.length === 0) {
      return schedaBlob;
    }

    await this.hydrateAttachmentsWhenMissing(healthRecord);

    const attachmentsById: Map<string, HealthRecordAttachment> = new Map(
      (healthRecord.attachments ?? []).map((attachment) => [
        attachment.id,
        attachment
      ])
    );

    const pdfBlobs: Blob[] = [];

    for (const attachmentId of orderedPdfAttachmentIds) {
      const attachment: HealthRecordAttachment | undefined =
        attachmentsById.get(attachmentId);

      if (!attachment || !isPdf(attachment.name)) {
        throw new Error(
          sntTranslate(
            marker("Impossibile includere l'allegato PDF nella stampa")
          )
        );
      }

      try {
        const downloadUrl: string = await lastValueFrom(
          this.storageBackend.download(attachment.storageId)
        );
        pdfBlobs.push(await urlToBlob(downloadUrl));
      } catch {
        throw new Error(
          sntTranslate(
            marker("Impossibile includere l'allegato PDF nella stampa")
          )
        );
      }
    }

    try {
      return await pdfMergeDocuments([schedaBlob, ...pdfBlobs]);
    } catch {
      throw new Error(
        sntTranslate(
          marker("Impossibile includere l'allegato PDF nella stampa")
        )
      );
    }
  }

  /**
   * SOAP send dialog (email, PDF, attachments) shared by clinical diary and filter detail.
   * Uses the loaded patient for all fields (single source of truth).
   */
  async sendSoapHealthRecordWithDialog(
    healthRecord: HealthRecordWithAttachmentsAndTicketExtended
  ): Promise<void> {
    const patient: Pet = await lastValueFrom(
      this.patientBackend.load({ id: healthRecord.patient.id })
    );

    if (!healthRecord.completedAt && healthRecord.type === "soap") {
      const confirm = await sntConfirm(
        sntTranslate(marker("Diario clinico")),
        sntTranslate(marker("Sicuro di inviare un scheda ancora non conclusa?"))
      );

      if (!confirm) {
        return;
      }
    }

    const parents = await lastValueFrom(
      this.healthRecordBackend.listAllChildrenByPatient(
        patient.id,
        healthRecord.id
      )
    );

    const customUsersEmails: Record<string, string> = {};

    let referringPhysician: Physician | undefined;
    let externalPhysician: Physician | undefined;

    if (patient.referringPhysician) {
      referringPhysician = await lastValueFrom(
        this.physicianBackend.load(patient.referringPhysician)
      );
    }

    if (patient.externalPhysician) {
      externalPhysician = await lastValueFrom(
        this.physicianBackend.load(patient.externalPhysician)
      );
    }

    await this.hydrateAttachmentsWhenMissing(healthRecord);

    const attachmentsList = healthRecord.attachments ?? [];

    const sendableAttachments = attachmentsList.filter(
      (x) => !(<AttachmentV2>x).signable && !(<AttachmentV2>x).signature
    );

    const signatureSignedAttachments = attachmentsList.filter(
      (x) => !!(<AttachmentV2>x).signature?.completedAt
    );

    const useTheSignedVersionOfTheDocument =
      signatureSignedAttachments.length > 0 && !!healthRecord.completedAt;

    await this.popupService.createEntity<{
      email: string;
      includeFollowUp: boolean;
      note?: string;
      physician?: string;
      referringPhysician?: string;
      externalPhysician?: string;
    }>({
      title: sntTranslate(marker("Invio scheda")),
      initialFormData: { email: patient.owner?.email || "" },
      modalConfig: {
        dialog: true,
        width: 620,
        height: 720
      },
      formItems: compact([
        {
          itemType: "simple",
          dataField: "email",
          editorType: "dxSelectBox",
          editorOptions: {
            dataSource: new DataSource({
              store: new CustomStore({
                key: "email",
                byKey: async (options) => {
                  return patient.relatives?.find(
                    (relative) => relative.email === options
                  );
                },
                load: async (options) => {
                  return {
                    data: patient.relatives,
                    totalCount: patient.relatives?.length
                  };
                }
              })
            }),
            searchEnabled: "true",
            valueExpr: "email",
            itemTemplate: (data: Customer & { email: string }) => {
              const firstLine = trim(
                `${data.lastName || ""} ${data.firstName || ""}`
              );
              const secondLine = trim(data.email);

              return `<div class="mr-1 text-xl">
                        <i class="fa-light fa-user-circle"></i>
                      </div>
                      <div class="flex flex-col items-start justify-center">
                        ${firstLine ? `<span>${firstLine}</span>` : ""}
                        <span>${secondLine}</span>
                      </div>`;
            },
            displayExpr: (data: Nullable<Customer & { email?: string }>) => {
              return data?.email || "";
            },
            acceptCustomValue: true,
            onCustomItemCreating: (dxEvent: {
              customItem: Partial<Customer> & { email?: string };
              text: string;
            }) => {
              const email = dxEvent.text;
              const id = uuid();

              dxEvent.customItem = {
                id,
                email: email,
                firstName: "",
                lastName: "",
                number: ""
              };
              customUsersEmails[id] = email;
            }
          },
          validationRules: [{ type: "required" }, { type: "email" }],
          label: {
            text: sntTranslate(marker("Email")),
            location: "top",
            showColon: false
          }
        },
        {
          itemType: "simple",
          dataField: "note",
          editorType: "dxTextArea",
          label: {
            text: sntTranslate(marker("Nota")),
            location: "top",
            showColon: false
          },
          editorOptions: { height: 80, maxLength: 500 },
          validationRules: []
        },
        {
          visible: parents.length > 0,
          dataField: "includeFollowUp",
          editorType: "dxCheckBox",
          label: {
            text: sntTranslate(marker("Includi i follow up")),
            showColon: false
          }
        },
        {
          itemType: "group",
          colCount: 2,
          items: sift([
            !!patient.physician
              ? {
                  dataField: "physician",
                  editorType: "dxCheckBox",
                  label: {
                    text: getCustomerNominative(patient.physician),
                    showColon: false
                  },
                  helpText: sntTranslate(marker("Medico curante"))
                }
              : null,
            !!referringPhysician
              ? {
                  dataField: "referringPhysician",
                  editorType: "dxCheckBox",
                  label: {
                    text: getCustomerNominative(referringPhysician),
                    showColon: false
                  },
                  helpText: sntTranslate(marker("Medico referente"))
                }
              : null,
            !!externalPhysician
              ? {
                  dataField: "externalPhysician",
                  editorType: "dxCheckBox",
                  label: {
                    text: getCustomerNominative(externalPhysician),
                    showColon: false
                  },
                  helpText: sntTranslate(marker("Medico curante (esterno)"))
                }
              : null
          ])
        },
        sendableAttachments.length > 0
          ? {
              itemType: "group",
              colCount: 1,
              items: sift([
                {
                  template: (dxEvent: any, element: HTMLElement) => {
                    element.innerHTML = `<div class="flex flex-row items-center mt-4">
                <span class="text-sm text-slate-400">${sntTranslate(
                  marker("Seleziona gli allegati da includere:")
                )}</span></div>`;
                  }
                },
                sendableAttachments.length > 1
                  ? <DevExpress.ui.dxForm.Item>{
                      dataField: "selectAllAttachments",
                      editorType: "dxCheckBox",
                      editorOptions: {
                        text: sntTranslate(marker("Seleziona tutti")),
                        onValueChanged: ({
                          dxEvent: _dxEvent,
                          dxForm
                        }: {
                          dxEvent: unknown;
                          dxForm: DxFormComponent;
                          formData: Record<string, boolean>;
                        }) => {
                          const formData = dxForm.formData as Record<
                            string,
                            boolean
                          >;
                          const selectAllAttachments: boolean =
                            formData["selectAllAttachments"];
                          sendableAttachments.forEach(({ id }) => {
                            dxForm.instance.updateData(
                              `attachment-${id}`,
                              selectAllAttachments
                            );
                          });
                        }
                      },
                      label: {
                        text: sntTranslate(marker("Seleziona tutti")),
                        location: "left",
                        showColon: false,
                        visible: false
                      }
                    }
                  : null,
                {
                  itemType: "group",
                  colCount: 2,
                  items: [
                    ...(sendableAttachments.map(
                      (x) =>
                        <DevExpress.ui.dxForm.Item>{
                          dataField: `attachment-${x.id}`,
                          editorType: "dxCheckBox",
                          editorOptions: {
                            text: x.name,
                            onValueChanged: ({
                              dxEvent: _dxEvent,
                              dxForm
                            }: {
                              dxEvent: unknown;
                              dxForm: DxFormComponent;
                              formData: any;
                            }) => {
                              if (sendableAttachments.length > 1) {
                                dxForm.instance.updateData(
                                  "selectAllAttachments",
                                  sendableAttachments.every(
                                    ({ id }) =>
                                      dxForm.formData[`attachment-${id}`]
                                  )
                                );
                              }
                            }
                          },
                          label: {
                            text: x.name,
                            location: "left",
                            showColon: false,
                            visible: false
                          }
                        }
                    ) || [])
                  ]
                },
                {
                  template: templatePrintCanBeLongerThenExpectedFn
                }
              ])
            }
          : null,
        useTheSignedVersionOfTheDocument
          ? {
              template: () =>
                `
                <div class="flex flex-row items-center gap-2">
                  <i class="fa-duotone fa-solid fa-circle-info"></i>

                  <span class="text-slate-400 italic text-xs">
                    ${sntTranslate(
                      marker(
                        "Al cliente verrà inviata la versione firmata della scheda"
                      )
                    )}
                  </span>
                </div>`
            }
          : null
      ]),
      create: async (data) => {
        await ensureAttachmentSelectionWithinLimit(data);

        let documentStorageId: string | undefined;

        switch (true) {
          case !useTheSignedVersionOfTheDocument: {
            const pdfBlob = await this.generatePdfBlob({
              patientId: patient.id,
              healthRecord: healthRecord,
              includeFollowUp: !!data.includeFollowUp,
              selectedAttachments: getSelectedAttachmentIds(data)
            });

            if (!healthRecord.storageId) {
              const uploadResult = await lastValueFrom(
                this.storageBackend.upload(
                  new File([pdfBlob], `${healthRecord.name}.pdf`),
                  null
                )
              );

              healthRecord.storageId = uploadResult.id;

              await lastValueFrom(
                this.healthRecordBackend.update(healthRecord.id, {
                  type: healthRecord.type,
                  storageId: uploadResult.id,
                  version: healthRecord.version
                })
              );
            } else {
              await lastValueFrom(
                this.storageBackend.update(
                  healthRecord.storageId,
                  `${healthRecord.name}.pdf`,
                  pdfBlob
                )
              );
            }

            documentStorageId = healthRecord.storageId;

            break;
          }
          case useTheSignedVersionOfTheDocument: {
            const lastSignedAttachment = last(signatureSignedAttachments)!;

            documentStorageId = lastSignedAttachment?.storageId;
            break;
          }
        }

        const attachments: Array<{
          contentType: string;
          fileName: string;
          storageId: string;
        }> = [];

        forOwn(data, (value, key) => {
          if (key && key.startsWith("attachment-") && value) {
            const attachmentId = last(key.split("-"));
            const attachment = attachmentsList.find(
              (x) => x.id === attachmentId
            )!;
            attachments.push({
              storageId: attachment.storageId,
              fileName: attachment.name,
              contentType:
                getMimeType(attachment.name) || "application/octet-stream"
            });
          }
        });

        if (data.referringPhysician) {
          const referringPhysicianEmail =
            await resolveReferringPhysicianSendEmail(referringPhysician, {
              popupService: this.popupService,
              physicianBackend: this.physicianBackend
            });

          if (referringPhysicianEmail) {
            await this.healthRecordBackend.send(
              <HealthRecord>(<unknown>healthRecord),
              {
                email: referringPhysicianEmail,
                storageId: healthRecord.storageId,
                attachments,
                note: <string | undefined>data.note
              }
            );
          }
        }

        if (data.externalPhysician) {
          const externalPhysicianEmail =
            await resolveExternalPhysicianSendEmail(externalPhysician, {
              popupService: this.popupService,
              physicianBackend: this.physicianBackend
            });

          if (externalPhysicianEmail) {
            await this.healthRecordBackend.send(
              <HealthRecord>(<unknown>healthRecord),
              {
                email: externalPhysicianEmail,
                storageId: healthRecord.storageId,
                attachments,
                note: <string | undefined>data.note
              }
            );
          }
        }

        if (data.physician) {
          if (!patient.physician?.id) {
            throw new Error("Medico curante non disponibile.");
          }

          const physicianMember = await lastValueFrom(
            this.memberBackend.load(patient.physician.id)
          );

          if (!physicianMember.email) {
            throw new Error("Il medico curante non ha impostata la mail.");
          }

          await this.healthRecordBackend.send(
            <HealthRecord>(<unknown>healthRecord),
            {
              email: <string>physicianMember.email,
              storageId: healthRecord.storageId,
              attachments,
              note: <string | undefined>data.note
            }
          );
        }

        await this.healthRecordBackend.send(
          <HealthRecord>(<unknown>healthRecord),
          {
            email: <string>data.email,
            storageId: documentStorageId,
            customerId: patient.owner?.id,
            attachments,
            note: <string | undefined>data.note
          }
        );
      }
    });
  }

  async healthRecordToHTML(
    healthRecord: HealthRecordWithAttachmentsAndTicketExtended,
    selectedAttachments?: string[]
  ): Promise<string> {
    await this.hydrateAttachmentsWhenMissing(healthRecord);
    const soapTemplate = await lastValueFrom(
      this.soapTemplateBackend.load(
        (<HealthRecord>(<unknown>healthRecord)).detail!.templateId!
      )
    );

    const pet = healthRecord.patient
      ? await lastValueFrom(
          this.patientBackend.load({ id: healthRecord.patient.id })
        )
      : undefined;

    let attachmentsList: HealthRecordAttachment[] =
      healthRecord.attachments ?? [];

    if (attachmentsList.length === 0) {
      attachmentsList = await lastValueFrom(
        this.healthRecordBackend.loadAttachments(healthRecord.id)
      );
      healthRecord.attachments = attachmentsList;
    }

    const attachments: HealthRecordAttachment[] =
      selectedAttachments && selectedAttachments.length > 0
        ? attachmentsList.filter(
            ({ id, name }) => selectedAttachments.includes(id) && isImage(name)
          )
        : [];

    const template = soapTemplateWithHealthRecordoToTemplateItems(
      soapTemplate,
      <HealthRecord>(<unknown>healthRecord),
      attachments,
      this.memberBackend,
      this.physicianBackend,
      this.templateService,
      this.patientBackend,
      this.customerBackend,
      undefined,
      pet,
      { checkedInAt: healthRecord.ticket?.checkedInAt }
    );

    const dataUrlAttachments = await this.#loadAttachmentDataUrlsForPrint(
      attachments ?? []
    );

    const { content } = await this.reportService.templateToHTML(
      template,
      {
        authors: healthRecord.staff?.map((x) => x.id) || []
      },
      {
        renderer: "data",
        componentsRenderers: [],
        headerTitle: healthRecord.name || "",
        useFakeTextData: false,
        onlyTextWithValue: true,
        dataUrlAttachments
      }
    );

    return content;
  }

  async #loadAttachmentDataUrlsForPrint(
    attachments: HealthRecordAttachment[]
  ): Promise<string[]> {
    if (attachments.length === 0) {
      return [];
    }

    return await Promise.all(
      attachments.map(({ storageId }) =>
        this.#attachmentToDataUrlForPrint(storageId)
      )
    );
  }

  async #attachmentToDataUrlForPrint(storageId: string): Promise<string> {
    const downloadUrl = await lastValueFrom(
      this.storageBackend.download(storageId)
    );

    return await urlToDataUrl(downloadUrl);
  }

  private async healthRecordToLettterHeadPdfBlob(
    healthRecord: HealthRecordWithAttachmentsAndTicketExtended,
    selectedAttachments?: string[]
  ): Promise<Blob> {
    const content = await this.healthRecordToHTML(
      healthRecord,
      selectedAttachments ?? []
    );
    return await this.documentService.letterHeadDocunentToPdf(content);
  }
}
